import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Phone, Clock, ArrowLeft, ExternalLink, MessageSquare, Navigation } from 'lucide-react';
import outlets from '../data/outlets.json';
import storeImages from '../data/storeImages.json';
import Header from './Header';
import Footer from './Footer';
import { gsap } from 'gsap';

import { useOutlets } from '../hooks/useOutlets';

const StorePage = () => {
  const { slug } = useParams();
  const { outlets, loading } = useOutlets();
  const store = outlets.find(o => o.slug === slug);

  useEffect(() => {
    if (store) {
      // Set Enhanced SEO tags
      const storeName = store.name.replace('PizzaToons ', '');
      const isPuneStore = store.zone.toLowerCase().includes('pune') || store.zone.toUpperCase() === 'PCMC';
      const locationTitleStr = isPuneStore ? `${store.zone}, Pune` : store.zone;
      const locationKeywordsStr = isPuneStore ? 'Pune' : store.zone;
      
      document.title = `${storeName} | Best Pizza in ${locationTitleStr} | PizzaToons`;
      
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', `Looking for the best pizza in ${store.zone}? Visit PizzaToons ${storeName} at ${store.address}. Order now via WhatsApp for fresh, delicious pizzas delivered hot!`);
      }

      // Handle Keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', `PizzaToons ${storeName}, Pizza in ${store.zone}, Best pizza ${locationKeywordsStr}, ${store.zone} pizza delivery, PizzaToons outlets, Pizza near me, ${locationKeywordsStr} pizza restaurant`);

      // Add LocalBusiness Schema
      const getStableIndex = (id, length) => {
        if (!id) return 0;
        const parsed = parseInt(id, 10);
        if (!isNaN(parsed)) return parsed % length;
        let hash = 0;
        const idStr = String(id);
        for (let i = 0; i < idStr.length; i++) {
          hash = idStr.charCodeAt(i) + ((hash << 5) - hash);
        }
        return Math.abs(hash) % length;
      };

      const stableIndex = getStableIndex(store.id, storeImages.length);
      const storeImage = storeImages[stableIndex] || '1.jpg';
      const postalCodeMatch = store.address ? store.address.match(/\b\d{6}\b/) : null;
      const postalCode = postalCodeMatch ? postalCodeMatch[0] : undefined;

      const formatPhoneForSchema = (phoneStr) => {
        if (!phoneStr) return "+917875550787";
        const cleaned = phoneStr.replace(/\D/g, '');
        if (cleaned.length === 10) return `+91${cleaned}`;
        if (cleaned.length === 12 && cleaned.startsWith('91')) return `+${cleaned}`;
        return phoneStr;
      };
      const formattedPhone = formatPhoneForSchema(store.phone);

      const schema = {
        "@context": "https://schema.org",
        "@type": "Restaurant",
        "@id": `https://pizzatoons.in/${store.slug}`,
        "name": store.name,
        "image": `https://pizzatoons.in/images/stores/${storeImage}`,
        "url": `https://pizzatoons.in/${store.slug}`,
        "telephone": formattedPhone,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": store.address || "Address coming soon...",
          "addressLocality": store.zone,
          "addressRegion": "Maharashtra",
          "addressCountry": "IN"
        },
        "geo": store.lat && store.lng ? {
          "@type": "GeoCoordinates",
          "latitude": parseFloat(store.lat),
          "longitude": parseFloat(store.lng)
        } : undefined,
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday"
            ],
            "opens": "11:00",
            "closes": "23:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Saturday",
              "Sunday"
            ],
            "opens": "11:00",
            "closes": "23:59"
          }
        ],
        "servesCuisine": "Pizza",
        "priceRange": "INR 80 - 200",
        "parentOrganization": {
          "@type": "Organization",
          "name": "PizzaToons",
          "url": "https://pizzatoons.in",
          "logo": "https://pizzatoons.in/pizzatoons-logo.png"
        }
      };

      if (!schema.geo) {
        delete schema.geo;
      }
      if (postalCode) {
        schema.address.postalCode = postalCode;
      }

      const script = document.createElement('script');
      script.id = 'local-business-schema';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema, null, 2);

      // Remove old schema if exists
      const oldScript = document.getElementById('local-business-schema');
      if (oldScript) {
        document.head.removeChild(oldScript);
      }
      document.head.appendChild(script);

      // Scroll to top
      window.scrollTo(0, 0);

      // GSAP Animations with fromTo to prevent React double-mount capture bug
      gsap.fromTo('.store-header', 
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }
      );
      gsap.fromTo('.store-card', 
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 1, delay: 0.2, stagger: 0.2, ease: 'power3.out' }
      );

      return () => {
        const s = document.getElementById('local-business-schema');
        if (s) document.head.removeChild(s);
      };
    }
  }, [slug, store]);

  if (loading && !store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Store Not Found</h1>
          <p className="mb-6 opacity-70">The store you are looking for doesn't exist or has been moved.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-all">
            <ArrowLeft size={18} /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;

  const getWhatsAppLink = (phone) => {
    const defaultNumber = '918007492424';
    if (!phone) return `https://wa.me/${defaultNumber}`;
    const cleaned = phone.replace(/\D/g, '');
    // If it's already 12 digits starting with 91, use as is. 
    // If it's 10 digits, add 91. 
    // If it's something else, try to fix or use default.
    const finalNumber = cleaned.length === 10 ? '91' + cleaned : cleaned;
    return `https://wa.me/${finalNumber}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary selection:text-primary-foreground">
      <Header isAlwaysScrolled={true} />
      
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-6">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 mb-8 text-sm opacity-60">
            <Link to="/" className="hover:text-primary">Home</Link>
            <span>/</span>
            <span className="text-primary font-medium">{store.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Column: Info */}
            <div className="space-y-10">
              <div className="store-header">
                <h1 className="text-3xl sm:text-5xl md:text-7xl font-black mb-6 tracking-tight leading-tight">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">PizzaToons Outlets</span> <br/>
                  <span className="opacity-90">{store.name.replace('PizzaToons ', '')}</span>
                </h1>
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-1.5 bg-primary/5 border border-primary/10 rounded-full text-sm font-semibold uppercase tracking-wider text-primary">
                    {store.zone}
                  </span>
                  {store.isHeadOffice && (
                    <span className="px-4 py-1.5 bg-orange-500/10 border border-orange-500/20 rounded-full text-sm font-semibold uppercase tracking-wider text-orange-600">
                      Head Office
                    </span>
                  )}
                  <span className="px-4 py-1.5 bg-green-500/10 border border-green-500/20 rounded-full text-sm font-semibold uppercase tracking-wider text-green-600">
                    Open Now
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Address Card */}
                <div className="store-card p-8 bg-card border border-border/50 rounded-3xl shadow-xl shadow-black/[0.02] hover:shadow-2xl hover:shadow-primary/5 transition-all duration-500 group">
                  <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 text-primary group-hover:scale-110 transition-transform duration-500">
                    <MapPin size={24} />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Visit Us</h3>
                  <p className="opacity-80 leading-relaxed mb-6">
                    {store.address || 'Address coming soon...'}
                  </p>
                  <a 
                    href={googleMapsUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary font-bold hover:gap-3 transition-all"
                  >
                    Get Directions <Navigation size={16} />
                  </a>
                </div>

                {/* Contact Card */}
                <div className="store-card p-8 bg-card border border-border/50 rounded-3xl shadow-xl shadow-black/[0.02] hover:shadow-2xl hover:shadow-primary/5 transition-all duration-500 group">
                  <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 text-primary group-hover:scale-110 transition-transform duration-500">
                    <Phone size={24} />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Call to Order</h3>
                  <div className="space-y-4">
                    <p className="text-2xl font-black tracking-tight">
                      {store.phone ? (
                        <a href={`tel:${store.phone}`} className="hover:text-primary transition-colors">
                          {store.phone}
                        </a>
                      ) : (
                        <span className="opacity-40">Not available</span>
                      )}
                    </p>
                    <p className="text-sm opacity-60">Available 11 AM - 11 PM</p>
                    <a 
                      href={getWhatsAppLink(store.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-600 rounded-xl text-sm font-bold hover:bg-green-500 hover:text-white transition-all w-fit"
                    >
                      <MessageSquare size={16} /> WhatsApp Us
                    </a>
                  </div>
                </div>
              </div>

              <div className="store-card p-8 bg-gradient-to-br from-primary to-primary/90 text-primary-foreground rounded-3xl shadow-2xl overflow-hidden relative group">
                <div className="relative z-10">
                  <h2 className="text-3xl font-black mb-4">Craving Pizza?</h2>
                  <p className="mb-8 opacity-90 text-lg">Order the best pizzas in town from {store.name.split(' ').pop()} outlet today!</p>
                  <div className="flex flex-wrap gap-4">
                    <a 
                      href={getWhatsAppLink(store.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-8 py-4 bg-primary text-primary-foreground rounded-2xl font-black hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
                    >
                      Order Now <MessageSquare size={18} />
                    </a>
                  </div>
                </div>
                {/* Decorative circle */}
                <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl group-hover:bg-white/20 transition-all duration-700"></div>
              </div>
            </div>

            {/* Right Column: Visual/Map */}
            <div className="lg:sticky lg:top-32 space-y-8">
              <div className="store-card aspect-square lg:aspect-video bg-muted rounded-[2.5rem] overflow-hidden border-8 border-card shadow-2xl relative">
                {/* Randomly pick a store image from the local folder */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-700"
                  style={{ 
                    backgroundImage: `url(/images/stores/${storeImages[Math.floor(Math.random() * storeImages.length)]})`,
                  }}
                >
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className="text-center p-6 backdrop-blur-md bg-white/10 rounded-3xl border border-white/20 text-white max-w-xs">
                      <h4 className="text-xl font-bold mb-2">Our Store</h4>
                      <p className="text-sm opacity-80">Experience the magic of PizzaToons in person!</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hours Card */}
              <div className="store-card p-8 bg-card border border-border/50 rounded-3xl shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                    <Clock size={20} />
                  </div>
                  <h3 className="text-xl font-bold">Opening Hours</h3>
                </div>
                <div className="space-y-4">
                  {[
                    { day: 'Monday - Friday', time: '11:00 AM - 11:00 PM' },
                    { day: 'Saturday', time: '11:00 AM - 12:00 AM' },
                    { day: 'Sunday', time: '11:00 AM - 12:00 AM' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-border/50 last:border-0">
                      <span className="font-medium opacity-70">{item.day}</span>
                      <span className="font-bold">{item.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StorePage;
